import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import { SectionCard } from "../../../../components/design-system/admin/SectionCard";
import { useBillingApi } from "../../../../common/hooks/use-billing-api.hook";
import { useWireAdminApi } from "../../../../common/hooks/use-wire-admin-api.hook";
import { VendorPlan } from "../../our-vendors/models/vendor.model";
import { VendorPlanOverridesPanel } from "./vendor-plan-overrides";
import { LegacyWireImportCard } from "./legacy-wire-import.card";

const labelClass = "mb-1.5 text-xs font-medium text-muted-foreground";

interface VendorPlanBillingProps {
    vendor: any;
    setVendor: (vendor: any) => void;
    submitted: boolean;
    /** The plans the vendor can be put on (loaded once by the page). */
    plans: VendorPlan[];
    /** Fields the server already saved on its own (re-pricing), so they are not "unsaved". */
    onServerSaved?: (patch: Record<string, any>) => void;
}

/**
 * The vendor's plan and what it allows: the plan, the product ceiling, per-vendor
 * overrides, and — for a vendor still paying an older price — the controls that
 * move them onto the plan's current price.
 */
export const VendorPlanBilling = ({ vendor, setVendor, submitted, plans, onServerSaved }: VendorPlanBillingProps) => {
    const { t } = useTranslation();
    const billingApi = useBillingApi();
    const wireAdminApi = useWireAdminApi();
    // Super-admin re-pricing: move a grandfathered vendor to the plan's current price.
    const [wireSaving, setWireSaving] = useState(false);
    const [prorate, setProrate] = useState(true);
    const [annualDiscountPercent, setAnnualDiscountPercent] = useState(0);
    const [maxProductsTouched, setMaxProductsTouched] = useState(false);

    useEffect(() => {
        // From the billing settings, which a super admin can read — the vendor-facing
        // billing config answers 401 here, and a 401 sends the session through a refresh.
        wireAdminApi.getSettings().then((c) => setAnnualDiscountPercent(c?.annualDiscountPercent || 0)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isEditMode = Boolean(vendor?._id);

    // The plan's current price for the vendor's wire interval (to decide whether
    // the vendor is grandfathered at a DIFFERENT price and needs the re-price block).
    const vendorPlan = plans.find((p) => p._id === (vendor?.plan?._id || vendor?.plan));
    // Re-pricing only applies on a paid plan — never on the free/default plan.
    const onPaidPlan = !!vendorPlan && !vendorPlan.isDefault;
    const wireIntervalV = vendor?.wireInterval === 'year' ? 'year' : 'month';
    const currentWirePlanPrice = vendorPlan
        ? (wireIntervalV === 'year'
            ? (vendorPlan.annualPrice != null ? vendorPlan.annualPrice : Math.round((vendorPlan.price || 0) * 12 * (1 - annualDiscountPercent / 100)))
            : (vendorPlan.price || 0))
        : undefined;
    // Only surface the wire re-price block on a paid plan when the snapshot differs
    // from the current plan price (nothing to do when they already match / use it).
    const showWireReprice = onPaidPlan
        && vendor?.paymentMethod === 'wire'
        && vendor?.wirePriceAmount != null && vendor.wirePriceAmount > 0
        && vendor.wirePriceAmount !== currentWirePlanPrice;

    // Wire: re-snapshot to the current plan price (applies at next renewal).
    const moveWireToCurrent = async () => {
        if (!vendor?._id) return;
        setWireSaving(true);
        try {
            const res = await billingApi.setVendorWirePrice(vendor._id); // omit amount → reset to current plan price
            setVendor({ ...vendor, wirePriceAmount: res.wirePriceAmount });
            onServerSaved?.({ wirePriceAmount: res.wirePriceAmount });
            toast.success(
                res.wirePriceAmount != null
                    ? t("ADMIN_VENDOREDIT.PLAN.WIRE_UPDATED", { amount: res.wirePriceAmount })
                    : t("ADMIN_VENDOREDIT.PLAN.WIRE_FOLLOWS_PLAN"),
            );
        } catch {
            toast.error(t("ADMIN_VENDOREDIT.PLAN.WIRE_FAILED"));
        } finally {
            setWireSaving(false);
        }
    };

    // Stripe: move the active subscription onto the plan's current price.
    const moveStripeToCurrent = async () => {
        if (!vendor?._id) return;
        setWireSaving(true);
        try {
            await billingApi.repriceVendorStripe(vendor._id, prorate);
            toast.success(prorate ? t("ADMIN_VENDOREDIT.PLAN.STRIPE_MOVED_NOW") : t("ADMIN_VENDOREDIT.PLAN.STRIPE_MOVED_RENEWAL"));
        } catch {
            toast.error(t("ADMIN_VENDOREDIT.PLAN.STRIPE_FAILED"));
        } finally {
            setWireSaving(false);
        }
    };

    const handleMaxProductsChange = (event) => {
        const { value } = event.target;
        setMaxProductsTouched(true);
        // Convert to number for numeric fields
        setVendor({ ...vendor, maxProducts: value === '' ? '' : parseInt(value, 10) });
    };
    const maxProductsInvalid = (submitted && !vendor.maxProducts) || (maxProductsTouched && !vendor.maxProducts);

    return (
        <SectionCard
            title={t("ADMIN_VENDOREDIT.PLAN.TITLE")}
            description={t("ADMIN_VENDOREDIT.PLAN.DESCRIPTION")}
            testId="vendor-plan-card"
        >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                    <Label htmlFor="vendor-plan" className={labelClass}>{t("ADMIN_VENDOREDIT.PLAN.PLAN")}</Label>
                    <Select
                        value={vendor?.plan?._id || vendor?.plan || ''}
                        onValueChange={(nextPlan) => {
                            const currentPlan = vendor?.plan?._id || vendor?.plan || '';
                            // Changing to a different plan resets per-vendor overrides so
                            // the vendor inherits the new plan (visible immediately in the
                            // panel below). Admin can re-add overrides before saving.
                            setVendor({
                                ...vendor,
                                plan: nextPlan,
                                ...(nextPlan !== currentPlan ? { planOverrides: undefined } : {}),
                            });
                        }}
                    >
                        <SelectTrigger id="vendor-plan" className="w-full" data-testid="vendor-plan-select">
                            <SelectValue placeholder={t("ADMIN_VENDOREDIT.PLAN.PLAN_PLACEHOLDER")} />
                        </SelectTrigger>
                        <SelectContent>
                            {plans.map((p) => (
                                <SelectItem key={p._id} value={p._id}>
                                    {p.isDefault ? t("ADMIN_VENDOREDIT.PLAN.PLAN_DEFAULT", { name: p.name }) : p.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="mt-1.5 text-xs text-muted-foreground">{t("ADMIN_VENDOREDIT.PLAN.PLAN_HINT")}</p>
                </div>

                <div>
                    <Label htmlFor="vendor-max-products" className={labelClass}>
                        {t("ADMIN_VENDOREDIT.PLAN.MAX_PRODUCTS")}
                    </Label>
                    <Input
                        id="vendor-max-products"
                        aria-invalid={maxProductsInvalid || undefined}
                        name="maxProducts"
                        placeholder="50"
                        value={vendor?.maxProducts ?? ""}
                        onChange={handleMaxProductsChange}
                        type="number"
                        min={1}
                        required
                    />
                    <p className="mt-1.5 text-xs text-muted-foreground">{t("ADMIN_VENDOREDIT.PLAN.MAX_PRODUCTS_HINT")}</p>
                </div>

                <div className="md:col-span-2">
                    <VendorPlanOverridesPanel
                        basePlan={
                            vendorPlan
                            // No explicit plan → backend falls back to the default plan,
                            // so show that plan's values as the inherited baseline.
                            || plans.find((p) => p.isDefault)
                        }
                        value={vendor?.planOverrides}
                        onChange={(next) => setVendor({ ...vendor, planOverrides: next })}
                    />
                </div>

                {/* Grandfathered-price control — only when the vendor's snapshot
                    differs from the plan's current price (otherwise nothing to do). */}
                {isEditMode && showWireReprice && (
                    <div className="md:col-span-2 rounded-lg border p-4" data-testid="vendor-wire-reprice">
                        <p className="mb-1 text-sm font-medium">{t("ADMIN_VENDOREDIT.PLAN.WIRE_TITLE")}</p>
                        <p className="mb-3 text-xs text-muted-foreground">
                            {t("ADMIN_VENDOREDIT.PLAN.WIRE_BODY", { amount: vendor.wirePriceAmount, current: currentWirePlanPrice })}
                        </p>
                        <Button variant="outline" disabled={wireSaving} onClick={moveWireToCurrent}>
                            {t("ADMIN_VENDOREDIT.PLAN.MOVE_TO_CURRENT")}
                        </Button>
                    </div>
                )}

                {/* TEMPORARY: vendors already paying by wire before plans existed. */}
                {isEditMode && onPaidPlan && !vendor?.paymentMethod && (
                    <LegacyWireImportCard vendor={vendor} setVendor={setVendor} plan={vendorPlan} onServerSaved={onServerSaved} />
                )}

                {isEditMode && onPaidPlan && vendor?.paymentMethod === 'stripe' && (
                    <div className="md:col-span-2 rounded-lg border p-4" data-testid="vendor-stripe-reprice">
                        <p className="mb-1 text-sm font-medium">{t("ADMIN_VENDOREDIT.PLAN.STRIPE_TITLE")}</p>
                        <p className="mb-3 text-xs text-muted-foreground">{t("ADMIN_VENDOREDIT.PLAN.STRIPE_BODY")}</p>
                        <Label className="mb-3 flex items-center gap-2 font-normal">
                            <Switch checked={prorate} onCheckedChange={(checked) => setProrate(checked)} />
                            <span className="text-[13px] text-muted-foreground">{t("ADMIN_VENDOREDIT.PLAN.PRORATE")}</span>
                        </Label>
                        <Button variant="outline" disabled={wireSaving} onClick={moveStripeToCurrent}>
                            {t("ADMIN_VENDOREDIT.PLAN.MOVE_TO_CURRENT")}
                        </Button>
                    </div>
                )}
            </div>
        </SectionCard>
    );
};
